import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, GraduationCap, Search, FileCheck, ArrowRight, Users } from 'lucide-react';

const TrainingAudits = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Export Compliance<br />Training & Audits
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Build and maintain a culture of compliance through comprehensive training programs and objective compliance assessments. Tailored training on U.S. and international export regulations.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Request Training Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Training Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Training: The Foundation of Effective Compliance
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl">
              Training is a fundamental and legally mandated aspect of any comprehensive import/export compliance program. Government authorities explicitly require that companies provide appropriate training to employees involved in controlled transactions. Inadequate training is consistently cited in enforcement actions and audit findings.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <Users className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regulatory Requirement</h3>
              <p className="text-gray-600">
                Both EAR and ITAR explicitly require training for persons involved in export transactions. Failure to provide adequate training constitutes a compliance program deficiency.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <GraduationCap className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Risk Mitigation</h3>
              <p className="text-gray-600">
                Well-trained employees recognize red flags, understand screening requirements, and follow proper procedures, significantly reducing inadvertent violations.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <FileCheck className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Audit Defense</h3>
              <p className="text-gray-600">
                Documented training programs demonstrate good faith compliance efforts and organizational commitment, crucial factors in penalty mitigation during enforcement proceedings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Comprehensive Training Programs
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Export Administration Regulations (EAR)</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive training on dual-use export controls, BIS requirements, and Commerce Control List classification.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">ECCN classification and jurisdiction determination</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">License requirements and exceptions (STA, ENC, etc.)</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Denied party screening and restricted destinations</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Deemed export requirements and technology transfers</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Red flag recognition and escalation procedures</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">International Traffic in Arms Regulations (ITAR)</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Specialized training on defense trade controls, DDTC requirements, and USML classification for manufacturers and exporters of defense articles.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">USML categories and specifically designed determination</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Registration requirements and annual reporting</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">License application procedures and agreements (TAA, MLA)</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Technical data control and foreign person access</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Technology Control Plan development</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">OFAC Sanctions Compliance</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Training on Treasury sanctions programs, screening requirements, and restricted party list management.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Sanctions programs overview (country and entity-based)</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">SDN List and other OFAC screening requirements</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">50% rule and beneficial ownership analysis</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">License application procedures (specific and general)</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Customs & Import Compliance</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive training on import procedures, HTS classification, valuation, and CBP compliance requirements.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">HTS classification methodologies and binding rulings</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Customs valuation and transfer pricing</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Country of origin determination and marking</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Free Trade Agreement qualification and preferential treatment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Training Delivery */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Flexible Training Delivery Options
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">On-Site Training</h3>
              <p className="text-gray-600">
                Customized training delivered at your facilities, tailored to your specific products, processes, and compliance challenges.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Virtual Training</h3>
              <p className="text-gray-600">
                Live, interactive webinars and virtual classroom sessions accommodating distributed teams and remote workforce.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Materials</h3>
              <p className="text-gray-600">
                Tailored training materials, quick reference guides, and desk procedures specific to your operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Audits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Export Compliance Audits & Assessments
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl">
              Objective evaluation of your trade compliance program to identify vulnerabilities, assess effectiveness, and provide actionable recommendations for enhancement.
            </p>
          </div>

          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <Search className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Compliance Audit</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thorough examination of your entire trade compliance program against regulatory requirements and industry best practices. Detailed findings report with risk ratings and remediation roadmap.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Audit Scope Includes:</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Written policies and procedures review</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Classification determination review and testing</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>License and exception utilization analysis</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Screening process evaluation</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Additional Review Areas:</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Recordkeeping and documentation practices</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Training program adequacy</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Technology control and deemed export procedures</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Management oversight and accountability</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <FileCheck className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Focused Compliance Assessment</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Targeted evaluation of specific compliance program elements or high-risk areas. Efficient assessment format for companies with established programs seeking validation or enhancement in particular areas.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="font-semibold text-gray-900 mb-2 text-sm">Focus Areas Include:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Classification accuracy</li>
                    <li>• Screening procedures</li>
                    <li>• License compliance</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2 text-sm">Operational Review:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Export transaction processing</li>
                    <li>• Documentation practices</li>
                    <li>• Recordkeeping systems</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2 text-sm">Program Assessment:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Policy effectiveness</li>
                    <li>• Training adequacy</li>
                    <li>• Management oversight</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Strengthen Your Compliance Program
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Invest in training and audits to build a robust, defensible trade compliance program.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Training or Audit Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingAudits;
